import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import {
  DialogShellComponent,
  FilledButtonComponent,
  OutlinedButtonComponent,
  SelectFieldComponent,
  TextFieldComponent,
} from '@custom-components';
import { UpdateInstallationGroup } from '@core/models';

/** Stored status values for an installation group (see dm.installation_group DDL). */
export const INSTALLATION_GROUP_STATUSES = ['Active', 'Inactive'] as const;

/** Current values used to pre-fill the edit form. */
export interface EditInstallationGroupDialogData {
  name: string;
  description?: string;
  status: string;
}

@Component({
  selector: 'xemex-edit-installation-group-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatSelectModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    SelectFieldComponent,
    TextFieldComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './edit-installation-group-dialog.component.html',
})
export class EditInstallationGroupDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<EditInstallationGroupDialogComponent>);
  private readonly data = inject<EditInstallationGroupDialogData>(MAT_DIALOG_DATA);

  readonly statuses = INSTALLATION_GROUP_STATUSES;

  readonly form = new FormGroup({
    name: new FormControl(this.data.name, {
      nonNullable: true,
      validators: Validators.required,
    }),
    description: new FormControl(this.data.description ?? '', { nonNullable: true }),
    status: new FormControl(this.data.status, {
      nonNullable: true,
      validators: Validators.required,
    }),
  });

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, description, status } = this.form.getRawValue();
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();

    // Only send the fields that actually changed, matching the endpoint's partial-update contract.
    const changes: UpdateInstallationGroup = {};
    if (trimmedName !== this.data.name) {
      changes.name = trimmedName;
    }
    if (trimmedDescription !== (this.data.description ?? '')) {
      changes.description = trimmedDescription || undefined;
    }
    if (status !== this.data.status) {
      changes.status = status;
    }

    this.dialogRef.close(changes);
  }
}
